import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Store, AlertCircle, CheckCircle, ShieldAlert, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { posUrl } from './storefront/storeBase';
import useNoIndex from '../hooks/useNoIndex';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdminMessage, setShowAdminMessage] = useState(false);

  // IP-approval lock screen state
  const [ipLock, setIpLock] = useState(null); // { ip, ipRequestToken, message }
  const [requesting, setRequesting] = useState(false);
  const [requestState, setRequestState] = useState(''); // '' | 'pending' | 'error'
  const [requestError, setRequestError] = useState('');

  const { login, isAuthenticated, adminCreated, clearAdminCreatedMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useNoIndex();

  // Redirect if already authenticated. The POS lives under /pos (login is at
  // /pos/login), so default there after sign-in unless a specific page was
  // requested.
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || posUrl('/');
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Show admin created message (initialize from adminCreated)
  useEffect(() => {
    if (adminCreated && !showAdminMessage) {
      // Use setTimeout to avoid synchronous setState warning
      const timer = setTimeout(() => setShowAdminMessage(true), 0);
      return () => clearTimeout(timer);
    }
  }, [adminCreated, showAdminMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (!result.success) {
      if (result.ipNotApproved) {
        setIpLock({ ip: result.ip, ipRequestToken: result.ipRequestToken, message: result.error });
        setRequestState('');
        setRequestError('');
      } else {
        setError(result.error);
      }
    }

    setLoading(false);
  };

  const handleRequestApproval = async () => {
    if (!ipLock?.ipRequestToken) return;
    setRequesting(true);
    setRequestError('');
    try {
      await axios.post(`${API}/ip-approvals/request`, {}, {
        headers: { Authorization: `Bearer ${ipLock.ipRequestToken}` },
      });
      setRequestState('pending');
    } catch (err) {
      setRequestState('error');
      setRequestError(err.response?.data?.detail || 'Could not send the approval request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const backToLogin = () => {
    setIpLock(null);
    setRequestState('');
    setRequestError('');
    setError('');
    setPassword('');
  };

  const dismissAdminMessage = () => {
    setShowAdminMessage(false);
    clearAdminCreatedMessage();
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* IP Approval Lock Screen */}
        {ipLock ? (
          <div data-testid="ip-lock-screen" className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-600 bg-opacity-20 mb-4">
                <ShieldAlert className="text-amber-400" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-white">Network Not Approved</h1>
              <p className="text-gray-400 text-sm mt-2">{ipLock.message}</p>
            </div>

            <div className="mb-6 p-3 bg-gray-700 bg-opacity-50 rounded-lg text-center">
              <p className="text-gray-400 text-xs">Your network address</p>
              <p className="text-white font-mono text-lg" data-testid="ip-lock-address">{ipLock.ip || 'Unknown'}</p>
            </div>

            {requestState === 'pending' ? (
              <div data-testid="ip-request-pending" className="mb-4 p-4 bg-green-900 bg-opacity-30 border border-green-500 rounded-lg text-center">
                <CheckCircle className="text-green-400 mx-auto mb-2" size={28} />
                <p className="text-green-300 text-sm font-semibold">Approval request sent!</p>
                <p className="text-green-300 text-xs mt-1 opacity-80">
                  Ask a manager to approve this network in Settings → IP Approvals. Once approved, sign in again.
                </p>
              </div>
            ) : (
              <>
                {requestState === 'error' && (
                  <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg flex items-center gap-2">
                    <AlertCircle className="text-red-400" size={18} />
                    <span className="text-red-300 text-sm">{requestError}</span>
                  </div>
                )}
                <Button
                  type="button"
                  data-testid="ip-request-approval-btn"
                  onClick={handleRequestApproval}
                  disabled={requesting}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3"
                >
                  {requesting ? (<><Loader2 className="mr-2 animate-spin" size={18} />Sending request...</>) : 'Request Approval'}
                </Button>
              </>
            )}

            <button
              type="button"
              data-testid="ip-back-to-login"
              onClick={backToLogin}
              className="w-full mt-4 text-gray-400 hover:text-gray-200 text-sm"
            >
              ← Back to sign in
            </button>
          </div>
        ) : (
        <>
        {/* Admin Created Alert */}
        {showAdminMessage && adminCreated && (
          <div className="mb-6 p-4 bg-green-900 bg-opacity-50 border border-green-500 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-400 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="text-green-400 font-semibold">Admin Account Created</h3>
                <p className="text-green-300 text-sm mt-1">
                  {adminCreated.message}
                </p>
                <p className="text-green-300 text-xs mt-2 opacity-75">
                  Username: <span className="font-mono">{adminCreated.username}</span>
                </p>
              </div>
              <button 
                onClick={dismissAdminMessage}
                className="text-green-400 hover:text-green-300"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-600 bg-opacity-20 mb-4">
              <Store className="text-purple-400" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white">Point of Sale</h1>
            <p className="text-gray-400 text-sm mt-1">Staff Login</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg flex items-center gap-2">
              <AlertCircle className="text-red-400" size={18} />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-300">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="bg-gray-700 border-gray-600 text-white"
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-xs text-gray-500">
            Contact your administrator if you need access
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default Login;
